import { useMemo, useState } from "react";
import { Building2, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { StarIcon } from "@/components/ui/StarIcon";
import { cn } from "@/lib/utils";
import type { EarningsCalendarItemOut } from "@/api/client";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function AgendaList({
  year,
  month,
  events,
  upcomingEvents,
  selectedId,
  onPrev,
  onNext,
  onSelectEvent,
}: {
  year: number;
  month: number;
  events: EarningsCalendarItemOut[];
  upcomingEvents: EarningsCalendarItemOut[];
  selectedId: number | null;
  onPrev: () => void;
  onNext: () => void;
  onSelectEvent: (item: EarningsCalendarItemOut) => void;
}) {
  const [companyFilter, setCompanyFilter] = useState<{
    id: number;
    label: string;
  } | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");

  const companyOptions = useMemo(() => {
    const seen = new Map<number, { id: number; label: string; symbol: string | null }>();
    for (const ev of upcomingEvents) {
      if (!seen.has(ev.company_id)) {
        seen.set(ev.company_id, {
          id: ev.company_id,
          label: ev.company_name,
          symbol: ev.primary_symbol,
        });
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [upcomingEvents]);

  const filteredCompanyEvents = useMemo(() => {
    if (!companyFilter) return [];
    return upcomingEvents
      .filter((ev) => ev.company_id === companyFilter.id)
      .sort((a, b) => a.announcement_date.localeCompare(b.announcement_date));
  }, [companyFilter, upcomingEvents]);

  const grouped = useMemo(() => {
    const source = companyFilter ? filteredCompanyEvents : events;
    const map = new Map<string, EarningsCalendarItemOut[]>();
    for (const ev of source) {
      const list = map.get(ev.announcement_date) ?? [];
      list.push(ev);
      map.set(ev.announcement_date, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [companyFilter, filteredCompanyEvents, events]);

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="flex flex-col">
      <div className="sticky top-0 z-20 px-2 py-2 border-b border-border bg-background space-y-2">
        {companyFilter ? (
          <div className="flex items-center gap-1.5">
            <div className="flex-1 min-w-0 inline-flex items-center gap-1.5 h-7 px-2 rounded-[4px] border border-primary bg-accent text-[12px]">
              <Building2 className="h-3 w-3 text-muted-foreground shrink-0" />
              <span className="font-mono font-semibold truncate">
                {companyFilter.label}
              </span>
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={() => setCompanyFilter(null)}
              aria-label="Clear company filter"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <Popover open={searchOpen} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="relative w-full h-7 rounded-[4px] border border-border bg-background pl-7 pr-2 text-[12px] text-left flex items-center text-muted-foreground"
              >
                <Building2 className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                Search company…
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] p-0"
              align="start"
            >
              <Command>
                <CommandInput
                  placeholder="Type a name or ticker…"
                  value={query}
                  onValueChange={setQuery}
                />
                <CommandList>
                  <CommandEmpty>No company found.</CommandEmpty>
                  <CommandGroup>
                    {companyOptions.map((c) => (
                      <CommandItem
                        key={c.id}
                        value={`${c.symbol ?? ""} ${c.label}`}
                        onSelect={() => {
                          setCompanyFilter({ id: c.id, label: c.label });
                          setQuery("");
                          setSearchOpen(false);
                          const nextEvent = upcomingEvents
                            .filter((ev) => ev.company_id === c.id)
                            .sort((a, b) =>
                              a.announcement_date.localeCompare(b.announcement_date),
                            )[0];
                          if (nextEvent) onSelectEvent(nextEvent);
                        }}
                      >
                        <span className="font-mono font-semibold w-14">
                          {c.symbol ?? "—"}
                        </span>
                        <span className="text-muted-foreground truncate">{c.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        {!companyFilter && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={onPrev}
              aria-label="Previous month"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <div className="font-semibold text-[13px] tabular-nums">
              {MONTH_NAMES[month - 1]} {year}
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              onClick={onNext}
              aria-label="Next month"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {grouped.length === 0 && (
        <div className="px-3 py-8 text-center text-[12px] text-muted-foreground">
          {companyFilter
            ? `No upcoming earnings for ${companyFilter.label}.`
            : `No earnings announcements in ${MONTH_NAMES[month - 1]}.`}
        </div>
      )}

      {grouped.map(([iso, dayEvents]) => {
        const d = new Date(iso + "T00:00:00");
        const isToday = iso === todayIso;
        return (
          <section key={iso}>
            <div
              className={cn(
                "sticky z-[5] px-3 py-1 text-[10px] font-mono uppercase tracking-wider",
                "bg-muted/40 backdrop-blur border-b border-border flex items-baseline gap-2",
                companyFilter ? "top-[44px]" : "top-[80px]",
              )}
            >
              <span className={cn("tabular-nums", isToday && "text-foreground font-semibold")}>
                {d.toLocaleDateString(undefined, {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  year: companyFilter ? "numeric" : undefined,
                })}
              </span>
              {isToday && <span className="text-[hsl(var(--chart-up))]">TODAY</span>}
              <span className="ml-auto text-muted-foreground">{dayEvents.length}</span>
            </div>

            {dayEvents.map((ev) => (
              <button
                key={ev.id}
                onClick={() => onSelectEvent(ev)}
                aria-pressed={ev.id === selectedId}
                className={cn(
                  "w-full text-left px-3 py-2 border-b border-border/60 min-h-[44px]",
                  "hover:bg-accent/60 transition-colors",
                  ev.id === selectedId && "bg-accent ring-1 ring-inset ring-primary",
                )}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="inline-flex items-baseline gap-1 min-w-0">
                    {ev.starred && <StarIcon className="h-2.5 w-2.5 self-center" />}
                    <span className="font-mono font-semibold text-[12px]">
                      {ev.primary_symbol ?? "—"}
                    </span>
                  </span>
                  <span className="text-[10px] text-muted-foreground font-mono tabular-nums">
                    {ev.fiscal_period ?? ""}
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                  {ev.company_name}
                </div>
              </button>
            ))}
          </section>
        );
      })}
    </div>
  );
}
